import React, { FC, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ThreeCircles } from 'react-loader-spinner';
import cn from 'classnames';

import useWindowSize from 'hooks/useWindowSize';
import { SearchList, Translation } from 'components/organisms';
import { SwipeTransition, SwitchTransitionWrapper } from 'components/molecules';
import { Button } from 'components/atoms';
import { BackIcon } from 'components/icons';
import { GET_TRANSLATIONS, POST_TRANSLATIONS } from 'queries';
import toastSuccess from 'utils/toastSuccess';
import toastError from 'utils/toastError';

import { TranslationType } from 'queries/types/TranslationsResultType';

import './Translations.scss';

const Translations: FC = () => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['translations'],
    queryFn: () => GET_TRANSLATIONS(),
  });

  const [showDetailedTranslations, setShowDetailedTranslations] = useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState<TranslationType | null>(null);
  const [loadingState, setLoadingState] = useState<boolean>(false);

  // eslint-disable-next-line
  const [windowWidth, windowHeight] = useWindowSize();

  const generateColumns = () => ['#', 'Updated By', 'Language', 'Last Update'];

  const generateEntries = () => {
    if (data?.data && 'data' in data.data) {
      return data?.data.data.map((item, index) => {
        const date = new Date(item.last_updated).toString().split('(')[0];
        return [index + 1, item.updated_by, item.language, <>{date}</>];
      });
    }
    return [];
  };

  const handleSelectTranslation = (index: number) => {
    if (data?.data && 'data' in data.data) {
      setSelectedTranslation(data.data.data[index]);
      setShowDetailedTranslations(true);
    }
  };

  const handleSubmitTranslations = () => {
    if (selectedTranslation) {
      setLoadingState(true);
      POST_TRANSLATIONS({
        language: selectedTranslation?.language,
        translations: selectedTranslation?.translations,
        examples: selectedTranslation?.examples,
      }).then((result) => {
        setLoadingState(false);
        if (result.success) {
          setShowDetailedTranslations(false);
          refetch().then((r) => r);
          setTimeout(() => setSelectedTranslation(null), 300);
          toastSuccess(`Translations updated successfully!`);
        } else {
          toastError(`Error while updating translations!`);
        }
        setLoadingState(false);
      });
    }
  };

  return (
    <section className={'section section-center translations no-scroll'}>
      {isLoading || !data ? (
        <ThreeCircles
          visible={true}
          height="100"
          width="100"
          color="white"
          wrapperStyle={{}}
          wrapperClass="history-list__loader"
        />
      ) : (
        <>
          <div
            className={'translations__control'}
            style={{ opacity: showDetailedTranslations ? 1 : 0 }}
          >
            <Button
              className={'translations__control-item'}
              primary
              onClick={() => {
                setShowDetailedTranslations(false);
                setTimeout(() => setSelectedTranslation(null), 300);
              }}
            >
              <BackIcon />
            </Button>
          </div>
          <Button
            onClick={handleSubmitTranslations}
            className={cn(
              'translations__submit',
              showDetailedTranslations ? 'translations__submit-in' : 'translations__submit-out',
            )}
            style={{
              bottom: `${0.03158 * windowHeight - 15}%`,
            }}
            disabled={loadingState}
            primary
          >
            {loadingState ? (
              <ThreeCircles
                visible={true}
                height="20"
                width="20"
                color="white"
                wrapperStyle={{}}
                wrapperClass=""
              />
            ) : (
              <>Update Translations</>
            )}
          </Button>
          <SwitchTransitionWrapper
            state={Boolean(selectedTranslation)}
            elements={[
              <p className="translations__head-text" key={'translations__head-text-1'}>
                {selectedTranslation?.language} language managing
              </p>,
              <p className="translations__head-text" key={'translations__head-text-2'}>
                Translations managing
              </p>,
            ]}
          />
          <SwipeTransition
            elements={[
              <Translation
                key={'translation-detailed'}
                data={selectedTranslation}
                setData={setSelectedTranslation}
              />,
              <SearchList
                className={'translations-list'}
                key={'translations-list'}
                rows={generateEntries()}
                columns={generateColumns()}
                searchBy={'All'}
                sortBy={'All'}
                rowClickFunc={(item, index) => handleSelectTranslation(index)}
              />,
            ]}
            hide={true}
            state={showDetailedTranslations}
            className={cn('translations-swipe')}
          />
        </>
      )}
    </section>
  );
};

export default Translations;
